import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Modal, Button, Form } from "react-bootstrap";
import { BiFilter } from "react-icons/bi";
import { GrPowerReset } from "react-icons/gr";
import PaginationComponent from "../../Component/Pagination/PaginationControls";
import ConfirmModal from "../../Component/ConfirmModel";
import apiCall from "./../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const emptyEditForm = {
  bankName: "",
  accountNumber: "",
  ifsc: "",
  holder: "",
  ac_type: "",
  branch: "",
};

const UserBankDetails = () => {
  const [bankList, setBankList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filterVisible, setFilterVisible] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentAccountNumber, setCurrentAccountNumber] = useState("");
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [updating, setUpdating] = useState(false);
  const [search, setSearch] = useState({
    search: "",
    uid: "",
    status: "",
    accountNumber: "",
    income_type: "",
  });

  const dataLimit = 10;

  const toggleFilter = () => {
    setFilterVisible(!filterVisible);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const fetchBankDetails = useCallback(
    async (page = currentPage) => {
      const filters = Object.fromEntries(
        Object.entries(search).filter(([, value]) => value !== "")
      );
      const queryParams = {
        page,
        limit: dataLimit,
        ...filters,
      };
      const queryString = new URLSearchParams(queryParams).toString();

      try {
        const { data, error } = await apiCall(
          "GET",
          `/all-user-bank-details?${queryString}`,
          null,
          false
        );
        if (error) {
          toast.error(error.message || "Failed to fetch bank details");
          return;
        }
        setBankList(data?.data || []);
        setTotalPages(data?.totalPages || 0);
        setTotalRecords(data?.totalRecords || 0);
      } catch (error) {
        toast.error(error.message || "Failed to fetch bank details");
      }
    },
    [currentPage, search]
  );

  const AddFilter = () => {
    setCurrentPage(1);
    fetchBankDetails(1);
  };

  const ResetFun = async () => {
    const resetState = {
      search: "",
      uid: "",
      status: "",
      accountNumber: "",
      income_type: "",
    };
    setSearch(resetState);
    setCurrentPage(1);

    const queryString = new URLSearchParams({
      page: 1,
      limit: dataLimit,
    }).toString();

    try {
      const { data, error } = await apiCall(
        "GET",
        `/all-user-bank-details?${queryString}`,
        null,
        false
      );
      if (error) {
        toast.error(error.message || "Failed to fetch bank details");
        return;
      }
      setBankList(data?.data || []);
      setTotalPages(data?.totalPages || 0);
      setTotalRecords(data?.totalRecords || 0);
    } catch (error) {
      toast.error(error.message || "Failed to fetch bank details");
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, [currentPage, fetchBankDetails]);

  const handleEditClick = (row) => {
    setSelectedRow(row);
    setCurrentAccountNumber(row.accountNumber || "");
    setEditForm({
      bankName: row.bankName || "",
      accountNumber: row.accountNumber || "",
      ifsc: row.ifsc || "",
      holder: row.holder || "",
      ac_type: row.ac_type || "",
      branch: row.branch || "",
    });
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedRow(null);
    setCurrentAccountNumber("");
    setEditForm(emptyEditForm);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveClick = (e) => {
    e.preventDefault();
    if (
      !editForm.bankName?.trim() ||
      !editForm.accountNumber?.trim() ||
      !editForm.ifsc?.trim() ||
      !editForm.holder?.trim() ||
      !editForm.ac_type ||
      !editForm.branch?.trim()
    ) {
      toast.error("Please fill all editable bank fields.");
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmUpdate = async () => {
    if (!selectedRow || updating) return;

    setUpdating(true);
    const payload = {
      uid: selectedRow.uid,
      currentAccountNumber,
      bankName: editForm.bankName.trim(),
      accountNumber: editForm.accountNumber.trim(),
      ifsc: editForm.ifsc.trim(),
      holder: editForm.holder.trim(),
      ac_type: editForm.ac_type,
      branch: editForm.branch.trim(),
    };

    const { data, error } = await apiCall(
      "POST",
      "/edit-user-bank-details",
      payload
    );

    setUpdating(false);

    if (error) {
      toast.error(error.message || "Failed to update bank details");
      return;
    }

    setShowConfirmModal(false);
    toast.success(data?.message || "User bank detail updated successfully.");
    handleEditModalClose();
    fetchBankDetails();
  };

  return (
    <React.Fragment>
      <Container>
        <div className="pagePath">
          <p className="m-0">
            Home / <span>User Bank Details</span>
          </p>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <p className="m-0" style={{ fontSize: "14px", opacity: 0.8 }}>
              Total records: {totalRecords}
            </p>
            <ExportButtons
              title="User Bank Details"
              disabled={!totalRecords}
              filters={Object.entries(search).filter(([, v]) => v !== "").map(([k, v]) => `${k}: ${v}`).join(", ")}
              columns={[
                { header: "Name", value: (r) => r.name },
                { header: "Username", value: (r) => r.username },
                { header: "UID", value: (r) => r.uid },
                { header: "Bank name", value: (r) => r.bankName },
                { header: "Account number", value: (r) => (r.accountNumber ? String(r.accountNumber) : "") },
                { header: "IFSC", value: (r) => r.ifsc },
                { header: "Holder", value: (r) => r.holder },
                { header: "Account type", value: (r) => r.ac_type },
                { header: "Branch", value: (r) => r.branch },
                { header: "Wallet type", value: (r) => r.wallet_type || r.income_type },
              ]}
              getRows={() =>
                collectPages(async (page, limit) => {
                  const filters = Object.fromEntries(Object.entries(search).filter(([, value]) => value !== ""));
                  const qs = new URLSearchParams({ ...filters, page, limit }).toString();
                  const { data, error } = await apiCall("GET", `/all-user-bank-details?${qs}`, null, false);
                  if (error) throw new Error(error.message || "Failed to fetch bank details");
                  return { rows: data?.data || [], pages: data?.totalPages || 1 };
                }, { limit: 500 })
              }
            />
          </div>
        </div>

        <Row id="filterRow">
          <span id="filterLogo">
            <div className="d-flex justify-content-between">
              <div onClick={toggleFilter}>
                <i>
                  <BiFilter />
                </i>
                Filter
              </div>
            </div>
          </span>

          {filterVisible && (
            <Row className="p-0 m-0">
              <Col lg="2" md="4">
                <div>
                  <input
                    type="text"
                    name="search"
                    placeholder="Search"
                    value={search.search}
                    onChange={handleChange}
                  />
                </div>
              </Col>

              <Col lg="2" md="4">
                <div>
                  <input
                    type="number"
                    name="uid"
                    placeholder="Search By UID"
                    value={search.uid}
                    onChange={handleChange}
                  />
                </div>
              </Col>

              <Col lg="2" md="4">
                <div>
                  <input
                    type="text"
                    name="accountNumber"
                    placeholder="Account Number"
                    value={search.accountNumber}
                    onChange={handleChange}
                  />
                </div>
              </Col>

              <Col lg="2" md="4">
                <div>
                  <select
                    name="status"
                    value={search.status}
                    onChange={handleChange}
                    className="form-control"
                    style={{ color: "white" }}
                  >
                    <option value="">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </Col>

              <Col lg="2" md="4">
                <div>
                  <select
                    name="income_type"
                    value={search.income_type}
                    onChange={handleChange}
                    className="form-control"
                    style={{ color: "white" }}
                  >
                    <option value="">All Wallet Types</option>
                    <option value="working">Working</option>
                    <option value="rental">Rental</option>
                  </select>
                </div>
              </Col>

              <div>
                <button className="save filter" onClick={AddFilter}>
                  Filter{" "}
                  <i>
                    <BiFilter />
                  </i>
                </button>
                <button className="cancel reset" onClick={ResetFun}>
                  Reset{" "}
                  <i>
                    <GrPowerReset />
                  </i>
                </button>
              </div>
            </Row>
          )}
        </Row>

        <div id="directIncomeDiv">
          <Row id="directTableRow" className="text-center">
            <Col md="12" className="p-0">
              <div style={{ width: "100%", overflowX: "auto" }}>
                <table>
                    <tr>
                      <th>S. No.</th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>UID</th>
                      <th>Bank Name</th>
                      <th>Account Number</th>
                      <th>IFSC</th>
                      <th>Holder</th>
                      <th>Account Type</th>
                      <th>Branch</th>
                      <th>Wallet Type</th>
                      <th>Action</th>
                    </tr>
                  <tbody>
                    {Array.isArray(bankList) && bankList.length > 0 ? (
                      bankList.map((row, index) => (
                          <tr key={`${row.uid}-${row.accountNumber}-${index}`}>
                            <td style={{ textAlign: "center" }}>
                              {(currentPage - 1) * dataLimit + index + 1}
                            </td>
                            <td style={{ textAlign: "center" }}>{row.name}</td>
                            <td style={{ textAlign: "center" }}>
                              {row.username}
                            </td>
                            <td style={{ textAlign: "center" }}>{row.uid}</td>
                            <td style={{ textAlign: "center" }}>
                              {row.bankName || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {row.accountNumber || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {row.ifsc || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {row.holder || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {row.ac_type || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {row.branch || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              {row.wallet_type || row.income_type || "N/A"}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <button
                                className="block-btn"
                                onClick={() => handleEditClick(row)}
                              >
                                Edit
                              </button>
                            </td>
                          </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="12" style={{ textAlign: "center" }}>
                          No bank details found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
        </div>

        <Row>
          <Col md="12">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePagination}
            />
          </Col>
        </Row>

        <Modal
          show={showEditModal}
          onHide={handleEditModalClose}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit User Bank Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedRow && (
              <Form onSubmit={handleSaveClick}>
                <Row className="mb-3">
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Name:</strong> {selectedRow.name}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Username:</strong> {selectedRow.username}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>UID:</strong> {selectedRow.uid}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1">
                      <strong>Wallet Type:</strong>{" "}
                      {selectedRow.wallet_type || selectedRow.income_type || "N/A"}
                    </p>
                  </Col>
                </Row>
                <hr />
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankName"
                        value={editForm.bankName}
                        onChange={handleEditFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="accountNumber"
                        value={editForm.accountNumber}
                        onChange={handleEditFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IFSC</Form.Label>
                      <Form.Control
                        type="text"
                        name="ifsc"
                        value={editForm.ifsc}
                        onChange={handleEditFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Holder</Form.Label>
                      <Form.Control
                        type="text"
                        name="holder"
                        value={editForm.holder}
                        onChange={handleEditFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Type</Form.Label>
                      <Form.Select
                        name="ac_type"
                        value={editForm.ac_type}
                        onChange={handleEditFormChange}
                        style={{ color: "white" }}
                        required
                      >
                        <option value="">Select Account Type</option>
                        <option value="Savings">Savings</option>
                        <option value="Current">Current</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Branch</Form.Label>
                      <Form.Control
                        type="text"
                        name="branch"
                        value={editForm.branch}
                        onChange={handleEditFormChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="d-flex justify-content-end">
                  <Button
                    variant="secondary"
                    onClick={handleEditModalClose}
                    className="me-2"
                  >
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit">
                    Update
                  </Button>
                </div>
              </Form>
            )}
          </Modal.Body>
        </Modal>

        <ConfirmModal
          show={showConfirmModal}
          handleClose={() => setShowConfirmModal(false)}
          handleConfirm={handleConfirmUpdate}
          title="Confirm Bank Details Update"
          body={
            <>
              <p>Are you sure you want to update bank details for this user?</p>
              {selectedRow && (
                <ul className="mb-0">
                  <li>
                    <strong>UID:</strong> {selectedRow.uid}
                  </li>
                  <li>
                    <strong>Username:</strong> {selectedRow.username}
                  </li>
                  <li>
                    <strong>Account Number:</strong> {editForm.accountNumber}
                  </li>
                </ul>
              )}
              {updating && (
                <p className="mt-2 mb-0 text-muted">Updating...</p>
              )}
            </>
          }
        />
      </Container>
    </React.Fragment>
  );
};

export default UserBankDetails;
